import { cn } from '@exam-room/utils';
import { useDisclosure } from '@shared/hooks';
import { forwardRef } from 'react';

interface ToggleButtonControlledProps extends Omit<React.ComponentProps<'button'>, 'children'> {
  open?: boolean;
  icon?: JSX.Element;
  label: string;
}

function ToggleButtonControlled(
  { open, onClick, icon, label, className, type = 'button', ...props }: ToggleButtonControlledProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      className={cn(
        '[&>svg]:hover:text-primary-900 flex cursor-pointer flex-col items-center space-y-1 p-2',
        className,
      )}
      onClick={onClick}
      ref={ref}
      type={type}
      {...props}
    >
      {icon}
      <p className='relative text-sm'>
        {label}
        {open ? (
          <span className='bg-primary absolute bottom-0 left-0 h-0.5 w-full translate-y-1.5' />
        ) : null}
      </p>
    </button>
  );
}

export const Controlled = forwardRef(ToggleButtonControlled);

interface ToggleButtonUncontrolledProps extends ToggleButtonControlledProps {
  children: (params: { open: boolean; toggle: () => void }) => JSX.Element;
}

function ToggleButtonUncontrolled(
  {
    open: initialOpen,
    icon,
    label,
    className,
    type = 'button',
    children,
    ...props
  }: ToggleButtonUncontrolledProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const [open, { toggle }] = useDisclosure(initialOpen);
  return (
    <>
      <button
        className={cn(
          '[&>svg]:hover:text-primary-900 flex cursor-pointer flex-col items-center space-y-1 p-2',
          className,
        )}
        onClick={toggle}
        ref={ref}
        type={type}
        {...props}
      >
        {icon}
        <p className='relative text-sm'>
          {label}
          {open ? (
            <span className='bg-primary absolute bottom-0 left-0 h-0.5 w-full translate-y-1.5' />
          ) : null}
        </p>
      </button>
      {children({ open, toggle })}
    </>
  );
}

export const Uncontrolled = forwardRef(ToggleButtonUncontrolled);
